/**
 * RegisterFamilyPage — krok 2 rejestracji.
 * Choice: utwórz nową rodzinę / dołącz po kodzie 6-znakowym.
 * Po sukcesie: ustawia rodzinę w store + navigate do /dashboard.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons } from '../../components/Icons';
import { showToast } from '../../components/ui/Toast';
import { IS_DEV_MODE } from '../../lib/firebase';
import { signOut, createFamily, joinFamilyByCode, getFamilyById, getAuthErrorMessage } from '../../features/auth/api';
import { useAuthStore } from '../../features/auth/authStore';
import { devSignIn, devSignOut } from '../../features/auth/useAuthSync';
import { auth } from '../../lib/firebase';
import { formatInviteCode, validateInviteCode } from '../../lib/utils';

type Choice = 'new' | 'join' | null;

export function RegisterFamilyPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const family = useAuthStore((s) => s.family);
  const pendingRegistration = useAuthStore((s) => s.pendingRegistration);
  const setUser = useAuthStore((s) => s.setUser);
  const setFamily = useAuthStore((s) => s.setFamily);

  // WSZYSTKIE hooks PRZED conditional return — Rules of Hooks (React #310).
  const [choice, setChoice] = useState<Choice>(null);
  const [familyName, setFamilyName] = useState('');
  const [creatorName, setCreatorName] = useState(user?.name || pendingRegistration?.name || '');
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSettingsForm, setShowSettingsForm] = useState(false);

  // Edge case Google login: user istnieje, family lookup w useAuthSync trwa,
  // gdy zwróci rodzinę — przeskocz do dashboard automatycznie (user nie musiał
  // się przebijać przez ten ekran).
  useEffect(() => {
    if (user && family) navigate('/dashboard', { replace: true });
  }, [user, family, navigate]);

  // Pierwsze 800ms po Google sign-in: pokazujemy loading zamiast formularza,
  // bo useAuthSync może jeszcze ściągać family. Bez tego — flicker.
  useEffect(() => {
    const t = setTimeout(() => setShowSettingsForm(true), 800);
    return () => clearTimeout(t);
  }, []);

  // Conditional return DOPIERO PO wszystkich hooks.
  if (user && !family && !showSettingsForm) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-slate-900">
        <div className="w-12 h-12 rounded-full border-4 border-sky-200 dark:border-slate-700 border-t-sky-600 animate-spin" aria-label="Ładowanie..." />
      </main>
    );
  }

  const handleBack = async () => {
    try {
      if (IS_DEV_MODE) {
        devSignOut();
      } else if (auth.currentUser) {
        await signOut();
      }
    } catch (err) {
      console.warn('[RegisterFamily] signOut error:', err);
    }
    navigate('/login');
  };

  const handleCreateFamily = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (familyName.trim().length < 2) {
      setError('Nazwa rodziny musi mieć min. 2 znaki');
      return;
    }
    if (creatorName.trim().length < 1) {
      setError('Podaj swoje imię');
      return;
    }
    setLoading(true);
    try {
      if (IS_DEV_MODE) {
        await new Promise((r) => setTimeout(r, 400));
        devSignIn();
        showToast('Rodzina utworzona (DEV)', 'success');
        navigate('/dashboard');
        return;
      }
      const uid = user?.uid;
      const userEmail = user?.email || pendingRegistration?.email || '';
      if (!uid) throw new Error('no-user');

      const { familyId, inviteCode: code } = await createFamily(uid, creatorName, userEmail, familyName);

      // Aktualizuj store ręcznie — useAuthSync też to wykryje, ale szybciej tu
      const familyData = await getFamilyById(familyId);
      if (familyData) {
        setUser({ uid, email: userEmail, name: creatorName });
        setFamily({
          id: familyId,
          name: familyName,
          inviteCode: code,
          creatorId: uid,
          members: (familyData.members as Record<string, { name: string; email: string }>) ?? {},
        });
      }
      showToast('Rodzina utworzona', 'success');
      navigate('/dashboard');
    } catch (err) {
      console.error('[RegisterFamily] create error:', err);
      setError(getAuthErrorMessage((err as { code?: string }).code));
    } finally {
      setLoading(false);
    }
  };

  const handleJoinFamily = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const valid = validateInviteCode(inviteCode);
    if (!valid) {
      setError('Kod ma 6 znaków (litery + cyfry)');
      return;
    }
    setLoading(true);
    try {
      if (IS_DEV_MODE) {
        if (valid !== 'DEV123') {
          setError('Nieprawidłowy kod rodziny (DEV: użyj DEV123)');
          return;
        }
        await new Promise((r) => setTimeout(r, 400));
        devSignIn();
        showToast('Dołączyłeś do rodziny (DEV)', 'success');
        navigate('/dashboard');
        return;
      }

      const uid = user?.uid;
      const userEmail = user?.email || pendingRegistration?.email || '';
      const userName = user?.name || pendingRegistration?.name || '';
      if (!uid) throw new Error('no-user');

      const familyId = await joinFamilyByCode(uid, userName, userEmail, valid);
      const familyData = await getFamilyById(familyId);
      if (familyData) {
        setFamily({
          id: familyId,
          name: (familyData.name as string) ?? '',
          members: (familyData.members as Record<string, { name: string; email: string }>) ?? {},
        });
      }
      showToast('Dołączyłeś do rodziny', 'success');
      navigate('/dashboard');
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      if (message === 'invalid-invite-code') {
        setError('Nie znaleziono rodziny z tym kodem');
      } else if (message === 'invite-code-expired') {
        setError('Ten kod wygasł — poproś o nowy');
      } else {
        setError(getAuthErrorMessage((err as { code?: string }).code));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-slate-100 dark:bg-slate-900 flex flex-col items-center justify-center p-4 safe-area-bottom">
      <div className="w-full max-w-md">
        <button
          onClick={handleBack}
          className="flex items-center gap-1.5 text-sm font-medium text-slate-600 dark:text-slate-400 mb-4 hover:text-slate-900 dark:hover:text-white transition-colors"
        >
          <Icons.ChevronLeft size={16} />
          Wróć do logowania
        </button>

        <div className="text-center mb-6 animate-in fade-in slide-in-from-top-4 duration-500">
          <div className="w-20 h-20 rounded-3xl mx-auto mb-3 bg-gradient-to-br from-sky-500 via-sky-600 to-emerald-500 shadow-lg shadow-sky-500/30 flex items-center justify-center">
            <Icons.Users size={44} className="text-white" strokeWidth={2} />
          </div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-slate-900 dark:text-white tracking-tight">
            Konfiguracja rodziny
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1.5 text-sm sm:text-base">
            Utwórz nową rodzinę albo dołącz do istniejącej
          </p>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-card-hover dark:shadow-2xl border border-slate-100 dark:border-slate-700 overflow-hidden animate-in fade-in zoom-in-95 duration-300">
          {/* Choice cards */}
          <div className="grid grid-cols-2 gap-3 p-4">
            <ChoiceCard
              active={choice === 'new'}
              onClick={() => setChoice('new')}
              icon={<Icons.Sparkles size={24} />}
              label="Nowa rodzina"
              tone="emerald"
            />
            <ChoiceCard
              active={choice === 'join'}
              onClick={() => setChoice('join')}
              icon={<Icons.Users size={24} />}
              label="Dołącz po kodzie"
              tone="sky"
            />
          </div>

          {/* New family form */}
          {choice === 'new' && (
            <form onSubmit={handleCreateFamily} className="px-6 pt-2 pb-6 space-y-4 animate-in slide-in-from-top-2 duration-200">
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">Twoje imię</label>
                <input
                  type="text"
                  value={creatorName}
                  onChange={(e) => setCreatorName(e.target.value)}
                  placeholder="Anna"
                  required
                  className="w-full px-4 py-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 transition-all text-base"
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                  Nazwa rodziny
                </label>
                <input
                  type="text"
                  value={familyName}
                  onChange={(e) => setFamilyName(e.target.value)}
                  placeholder="Rodzina Kowalskich"
                  required
                  className="w-full px-4 py-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 transition-all text-base"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 leading-relaxed">
                  Po utworzeniu dostaniesz kod zaproszenia 6-znakowy do udostępnienia bliskim.
                </p>
              </div>

              {error && (
                <div className="flex items-start gap-2.5 p-3.5 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-xl text-sm text-rose-600 dark:text-rose-300">
                  <Icons.AlertCircle size={16} className="shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 disabled:bg-emerald-400 text-white font-bold rounded-xl transition-colors shadow-lg shadow-emerald-600/25 text-base"
              >
                {loading ? 'Tworzenie...' : 'Utwórz rodzinę'}
              </button>
            </form>
          )}

          {/* Join family form */}
          {choice === 'join' && (
            <form onSubmit={handleJoinFamily} className="px-6 pt-2 pb-6 space-y-4 animate-in slide-in-from-top-2 duration-200">
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">Kod rodziny</label>
                <input
                  type="text"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
                  placeholder="ABC-123"
                  maxLength={7}
                  required
                  className="w-full px-4 py-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 transition-all text-2xl font-mono font-bold tracking-[0.3em] text-center uppercase"
                  autoComplete="off"
                  autoCapitalize="characters"
                  inputMode="text"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 leading-relaxed">
                  Poproś osobę która utworzyła rodzinę o kod 6-znakowy.
                  {IS_DEV_MODE && <span className="block mt-1 text-amber-600 dark:text-amber-400 font-medium">DEV: użyj kodu DEV-123</span>}
                </p>
              </div>

              {inviteCode.length >= 6 && (
                <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
                  Sformatowany kod: <code className="font-mono font-bold text-sky-600 dark:text-sky-300">{formatInviteCode(inviteCode.replace(/-/g, ''))}</code>
                </p>
              )}

              {error && (
                <div className="flex items-start gap-2.5 p-3.5 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-xl text-sm text-rose-600 dark:text-rose-300">
                  <Icons.AlertCircle size={16} className="shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-sky-600 hover:bg-sky-700 active:bg-sky-800 disabled:bg-sky-400 text-white font-bold rounded-xl transition-colors shadow-lg shadow-sky-600/25 text-base"
              >
                {loading ? 'Dołączanie...' : 'Dołącz do rodziny'}
              </button>
            </form>
          )}

          {!choice && (
            <div className="px-6 pb-6 text-center text-sm text-slate-500 dark:text-slate-400">
              Wybierz opcję powyżej, żeby kontynuować
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

interface ChoiceCardProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  tone: 'sky' | 'emerald';
}

function ChoiceCard({ active, onClick, icon, label, tone }: ChoiceCardProps) {
  const activeRing = tone === 'emerald' ? 'ring-emerald-500/40 bg-emerald-50 dark:bg-emerald-900/20' : 'ring-sky-500/40 bg-sky-50 dark:bg-sky-900/20';
  const iconColor = tone === 'emerald' ? 'text-emerald-600 dark:text-emerald-400' : 'text-sky-600 dark:text-sky-400';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all ${
        active
          ? `border-transparent ring-4 ${activeRing}`
          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
      }`}
    >
      <div className={iconColor}>{icon}</div>
      <span className="text-sm font-semibold text-slate-900 dark:text-white">{label}</span>
    </button>
  );
}

export default RegisterFamilyPage;
